import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@ui/app/store';
import type { ValueSetListItem } from '../api/terminologyApi';

export type SearchMode = 'term-to-code' | 'code-to-code' | 'context-resolution' | 'document-search' | 'entity-mapping';
export type DocumentSearchMode = '' | 'fast-search' | 'accurate-search';

export interface PendingFile {
    file: File;
    fileStatus: 'pending' | 'processing' | 'completed' | 'failed';
}

export interface DocumentResult {
    document_name: string;
    entities?: Array<{
        text: string;
        coding_results: Array<{
            document: string;
            score: number;
            cmetadata: {
                vocabulary_id: string;
                concept_code: string;
                [key: string]: unknown;
            };
        }>;
    }>;
    [key: string]: unknown;
}

export interface TerminologySearchState {
    searchMode: SearchMode;
    documentSearchMode: DocumentSearchMode;
    documentSearchConfig: unknown | null;
    availableCodeSystems: string[];
    allCodeSystems: string[];
    selectedCodeSystems: string[];
    isFetchingCodeSystems: boolean;
    availableValueSets: ValueSetListItem[];
    selectedValueSets: string[];
    valueSetMaps: Record<string, unknown>;
    sourceVocab: string;
    targetVocab: string;
    isFilterPanelOpen: boolean;
    domains: string[];
    searchType: string;
    concepts: string[];
    validity: boolean;
    filterByValueSet: boolean;
    topK: number;
    confidenceRange: [number, number];
    pendingFiles: PendingFile[];
    documentSearchResults: DocumentResult[];
    currentDocumentFile: PendingFile | Record<string, never>;
    currentDocumentSearchResults: DocumentResult | Record<string, never>;
    completedDocumentFiles: PendingFile[];
    sidebarActiveTab: 'search' | 'api';
    apiHelpSubTab: 'product-info' | 'api-access';
}

const initialState: TerminologySearchState = {
    searchMode: 'term-to-code',
    documentSearchMode: '',
    documentSearchConfig: null,
    availableCodeSystems: [],
    allCodeSystems: [],
    selectedCodeSystems: [],
    isFetchingCodeSystems: true,
    availableValueSets: [],
    selectedValueSets: [],
    valueSetMaps: {},
    sourceVocab: '',
    targetVocab: '',
    isFilterPanelOpen: false,
    domains: [],
    searchType: '',
    concepts: [],
    validity: true,
    filterByValueSet: false,
    topK: 20,
    confidenceRange: [0, 1],
    pendingFiles: [],
    documentSearchResults: [],
    currentDocumentFile: {},
    currentDocumentSearchResults: {},
    completedDocumentFiles: [],
    sidebarActiveTab: 'search',
    apiHelpSubTab: 'product-info',
};

export const terminologySearchSlice = createSlice({
    name: 'terminologySearch',
    initialState,
    reducers: {
        setSearchMode: (state, action: PayloadAction<SearchMode>) => {
            state.searchMode = action.payload;
        },
        setDocumentSearchMode: (state, action: PayloadAction<DocumentSearchMode>) => {
            state.documentSearchMode = action.payload;
        },
        setDocumentSearchConfig: (state, action: PayloadAction<unknown | null>) => {
            state.documentSearchConfig = action.payload;
        },
        setAvailableCodeSystems: (state, action: PayloadAction<string[]>) => {
            state.availableCodeSystems = action.payload;
        },
        setAllCodeSystems: (state, action: PayloadAction<string[]>) => {
            state.allCodeSystems = action.payload;
        },
        setSelectedCodeSystems: (state, action: PayloadAction<string[]>) => {
            state.selectedCodeSystems = action.payload;
        },
        setFetchingCodeSystems: (state, action: PayloadAction<boolean>) => {
            state.isFetchingCodeSystems = action.payload;
        },
        setAvailableValueSets: (state, action: PayloadAction<ValueSetListItem[]>) => {
            state.availableValueSets = action.payload;
        },
        setSelectedValueSets: (state, action: PayloadAction<string[]>) => {
            state.selectedValueSets = action.payload;
        },
        setValueSetMaps: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.valueSetMaps = action.payload;
        },
        setSourceVocab: (state, action: PayloadAction<string>) => {
            state.sourceVocab = action.payload;
        },
        setTargetVocab: (state, action: PayloadAction<string>) => {
            state.targetVocab = action.payload;
        },
        setFilterPanelOpen: (state, action: PayloadAction<boolean>) => {
            state.isFilterPanelOpen = action.payload;
        },
        setDomains: (state, action: PayloadAction<string[]>) => {
            state.domains = action.payload;
        },
        setSearchType: (state, action: PayloadAction<string>) => {
            state.searchType = action.payload;
        },
        setConcepts: (state, action: PayloadAction<string[]>) => {
            state.concepts = action.payload;
        },
        setValidity: (state, action: PayloadAction<boolean>) => {
            state.validity = action.payload;
        },
        setFilterByValueSet: (state, action: PayloadAction<boolean>) => {
            state.filterByValueSet = action.payload;
        },
        setTopK: (state, action: PayloadAction<number>) => {
            state.topK = action.payload;
        },
        setConfidenceRange: (state, action: PayloadAction<[number, number]>) => {
            state.confidenceRange = action.payload;
        },
        setPendingFiles: (state, action: PayloadAction<PendingFile[]>) => {
            state.pendingFiles = action.payload;
        },
        setDocumentSearchResults: (state, action: PayloadAction<DocumentResult[]>) => {
            state.documentSearchResults = action.payload;
        },
        setCurrentDocumentFile: (state, action: PayloadAction<PendingFile | Record<string, never>>) => {
            state.currentDocumentFile = action.payload;
        },
        setCurrentDocumentSearchResults: (state, action: PayloadAction<DocumentResult | Record<string, never>>) => {
            state.currentDocumentSearchResults = action.payload;
        },
        setCompletedDocumentFiles: (state, action: PayloadAction<PendingFile[]>) => {
            state.completedDocumentFiles = action.payload;
        },
        setSidebarActiveTab: (state, action: PayloadAction<'search' | 'api'>) => {
            state.sidebarActiveTab = action.payload;
        },
        setApiHelpSubTab: (state, action: PayloadAction<'product-info' | 'api-access'>) => {
            state.apiHelpSubTab = action.payload;
        },
        /** Derive availableCodeSystems from document search result entities (vocabulary_id). */
        updateAvailableCodeSystemsFromDocumentResult: (
            state,
            action: PayloadAction<DocumentResult | Record<string, never> | undefined>
        ) => {
            const result = action.payload;
            if (!result || !('entities' in result) || !result.entities) {
                return;
            }
            const ids = new Set<string>();
            for (const entity of result.entities) {
                for (const cr of entity.coding_results || []) {
                    const cs = cr.cmetadata?.vocabulary_id;
                    if (cs) ids.add(cs);
                }
            }
            state.availableCodeSystems = Array.from(ids).sort();
        },
        /** Reset filter-related state when search mode changes (called from provider effect). */
        resetFiltersForSearchMode: state => {
            state.selectedCodeSystems = [];
        },
    },
});

export const {
    setSearchMode,
    setDocumentSearchMode,
    setDocumentSearchConfig,
    setAvailableCodeSystems,
    setAllCodeSystems,
    setSelectedCodeSystems,
    setFetchingCodeSystems,
    setAvailableValueSets,
    setSelectedValueSets,
    setValueSetMaps,
    setSourceVocab,
    setTargetVocab,
    setFilterPanelOpen,
    setDomains,
    setSearchType,
    setConcepts,
    setValidity,
    setFilterByValueSet,
    setTopK,
    setConfidenceRange,
    setPendingFiles,
    setDocumentSearchResults,
    setCurrentDocumentFile,
    setCurrentDocumentSearchResults,
    setCompletedDocumentFiles,
    setSidebarActiveTab,
    setApiHelpSubTab,
    updateAvailableCodeSystemsFromDocumentResult,
    resetFiltersForSearchMode,
} = terminologySearchSlice.actions;

// Selectors (granular to avoid unnecessary re-renders)
export const selectSearchMode = (s: RootState) => s.terminologySearch.searchMode;
export const selectDocumentSearchMode = (s: RootState) => s.terminologySearch.documentSearchMode;
export const selectDocumentSearchConfig = (s: RootState) => s.terminologySearch.documentSearchConfig;
export const selectAvailableCodeSystems = (s: RootState) => s.terminologySearch.availableCodeSystems;
export const selectAllCodeSystems = (s: RootState) => s.terminologySearch.allCodeSystems;
export const selectSelectedCodeSystems = (s: RootState) => s.terminologySearch.selectedCodeSystems;
export const selectIsFetchingCodeSystems = (s: RootState) => s.terminologySearch.isFetchingCodeSystems;
export const selectAvailableValueSets = (s: RootState) => s.terminologySearch.availableValueSets;
export const selectSelectedValueSets = (s: RootState) => s.terminologySearch.selectedValueSets;
export const selectValueSetMaps = (s: RootState) => s.terminologySearch.valueSetMaps;
export const selectSourceVocab = (s: RootState) => s.terminologySearch.sourceVocab;
export const selectTargetVocab = (s: RootState) => s.terminologySearch.targetVocab;
export const selectIsFilterPanelOpen = (s: RootState) => s.terminologySearch.isFilterPanelOpen;
export const selectDomains = (s: RootState) => s.terminologySearch.domains;
export const selectSearchType = (s: RootState) => s.terminologySearch.searchType;
export const selectConcepts = (s: RootState) => s.terminologySearch.concepts;
export const selectValidity = (s: RootState) => s.terminologySearch.validity;
export const selectFilterByValueSet = (s: RootState) => s.terminologySearch.filterByValueSet;
export const selectTopK = (s: RootState) => s.terminologySearch.topK;
export const selectConfidenceRange = (s: RootState) => s.terminologySearch.confidenceRange;
export const selectPendingFiles = (s: RootState) => s.terminologySearch.pendingFiles;
export const selectDocumentSearchResults = (s: RootState) => s.terminologySearch.documentSearchResults;
export const selectCurrentDocumentFile = (s: RootState) => s.terminologySearch.currentDocumentFile;
export const selectCurrentDocumentSearchResults = (s: RootState) => s.terminologySearch.currentDocumentSearchResults;
export const selectCompletedDocumentFiles = (s: RootState) => s.terminologySearch.completedDocumentFiles;
export const selectSidebarActiveTab = (s: RootState) => s.terminologySearch.sidebarActiveTab;
export const selectApiHelpSubTab = (s: RootState) => s.terminologySearch.apiHelpSubTab;

export default terminologySearchSlice.reducer;
